/**
 * Channel lifecycle actions — archive / unarchive / leave / delete — shared by
 * the thread header's settings menu and the channel list's per-row menu.
 *
 * Permission model (the server is the authority; these only decide what to SHOW):
 *  - delete:   channel owner/admin, org admin, super admin. Never member DMs
 *              (dm_member_id set) — those can only be archived (server 400s).
 *  - archive:  channel owner/admin and org admins.
 *  - leave:    any member of a group channel.
 * Success is confirmed with a toast. Any 4xx is reported through `notify` with
 * the server's `detail`, which callers render inline next to the control.
 */

import React, { useContext } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { messagingApi, Channel, ChannelDetail } from '@/services/messagingApi';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useOptionalAuth } from '@/contexts/AuthContext';
import RoleContext from '@/contexts/RoleContext';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import type { Notice } from './InlineNotice';
import { toast } from '../ui/use-toast';
import { getCurrentUserId } from '../../utils/auth';

const ORG_ADMIN_ROLES = new Set(['super_admin', 'ops_admin', 'org_admin']);

/**
 * True when the viewer is an org admin / super admin in the active org. Reads
 * the contexts without their throwing hooks so the messaging components still
 * render where no Auth/Role provider is mounted (tests, popouts).
 */
export function useIsOrgAdmin(): boolean {
  const role = useContext(RoleContext);
  const auth = useOptionalAuth();
  const user = auth?.user as any;
  const effective = role?.currentRole || user?.role || user?.cbo_role || null;
  return (
    (!!effective && ORG_ADMIN_ROLES.has(String(effective))) ||
    user?.is_super_admin === true ||
    user?.is_platform_admin === true
  );
}

export interface ChannelPermissions {
  isMemberDM: boolean;
  isGroupChannel: boolean;
  canDelete: boolean;
  canArchive: boolean;
  canLeave: boolean;
}

/**
 * What lifecycle actions to offer for a channel. `members` is only known on a
 * ChannelDetail; without it (the channel list) owner/admin is unknown, so only
 * org admins see delete there and archive is offered to everyone (server gates).
 */
export function getChannelPermissions(
  channel: Channel | ChannelDetail,
  isOrgAdmin: boolean,
  currentUserId: number | null = getCurrentUserId(),
): ChannelPermissions {
  const isMemberDM = channel.dm_member_id != null;
  const isGroupChannel = !channel.is_dm && channel.channel_type !== 'direct';
  const members = 'members' in channel ? channel.members : null;
  const me = members?.find((m) => m.user_id === currentUserId);
  const isChannelManager = me?.role === 'owner' || me?.role === 'admin';
  const roleKnown = members !== null;

  return {
    isMemberDM,
    isGroupChannel,
    canDelete: !isMemberDM && (isOrgAdmin || isChannelManager),
    canArchive: isMemberDM || isOrgAdmin || isChannelManager || !roleKnown,
    canLeave: isGroupChannel && !isMemberDM && (!roleKnown || !!me) && me?.role !== 'owner',
  };
}

export function useChannelLifecycle(
  channel: Channel | ChannelDetail,
  onRemoved?: () => void,
  /** Receives failures only; successes are toasts. */
  notify?: (notice: Notice) => void,
) {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['messaging-channels'] });
    queryClient.invalidateQueries({ queryKey: ['dm-channels'] });
    queryClient.invalidateQueries({ queryKey: ['channel-detail', channel.id] });
  };
  const ok = (title: string) => toast({ title });
  const fail = (title: string) => (error: Error) =>
    notify?.({ kind: 'error', title, description: error?.message });

  const archive = useMutation({
    mutationFn: () => messagingApi.archiveChannel(channel.id),
    onSuccess: () => {
      refresh();
      ok(t('messaging.channel.archived', 'Channel archived'));
      onRemoved?.();
    },
    onError: fail(t('messaging.channel.archiveFailed', 'Could not archive channel')),
  });

  const unarchive = useMutation({
    mutationFn: () => messagingApi.unarchiveChannel(channel.id),
    onSuccess: () => {
      refresh();
      ok(t('messaging.channel.unarchived', 'Channel restored'));
    },
    onError: fail(t('messaging.channel.unarchiveFailed', 'Could not restore channel')),
  });

  const leave = useMutation({
    mutationFn: () => messagingApi.leaveChannel(channel.id),
    onSuccess: () => {
      refresh();
      ok(t('messaging.channel.left', 'You left the channel'));
      onRemoved?.();
    },
    onError: fail(t('messaging.channel.leaveFailed', 'Could not leave channel')),
  });

  // Delete errors are shown inside the confirm dialog (remove.error), so no notify.
  const remove = useMutation({
    mutationFn: () => messagingApi.deleteChannel(channel.id),
    onSuccess: () => {
      refresh();
      queryClient.removeQueries({ queryKey: ['channel-messages', channel.id] });
      ok(t('messaging.channel.deleted', 'Channel deleted'));
      onRemoved?.();
    },
  });

  return { archive, unarchive, leave, remove };
}

interface DeleteChannelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channelName: string;
  isPending: boolean;
  onConfirm: () => void;
  /** Server refusal (e.g. 403/400 detail), shown inside the dialog. */
  error?: string | null;
}

/** Destructive confirm for deleting a channel for everyone. */
export function DeleteChannelDialog({
  open,
  onOpenChange,
  channelName,
  isPending,
  onConfirm,
  error,
}: DeleteChannelDialogProps) {
  const { t } = useAppTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('messaging.channel.deleteTitle', 'Delete #{{name}}?', { name: channelName })}
          </DialogTitle>
          <DialogDescription>
            {t(
              'messaging.channel.deleteDescription',
              'This hides the channel and its messages for everyone in it. Members will no longer see it in their list. If you only want it out of the way, archive it instead.',
            )}
          </DialogDescription>
        </DialogHeader>
        {error && (
          <div
            role="alert"
            className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200"
          >
            <div className="font-medium">{t('messaging.channel.deleteFailed', 'Could not delete channel')}</div>
            <div className="text-xs">{error}</div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            {t('messaging.channel.cancel', 'Cancel')}
          </Button>
          <Button variant="danger" onClick={onConfirm} disabled={isPending}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('messaging.channel.delete', 'Delete channel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

